import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc, getDocFromServer, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { AUTH_BEFORE_LOGOUT_EVENT, signOutCleanly } from '../lib/authSession';
import { registerCurrentDevice, watchCurrentDeviceRevocation, evaluateDevice } from '../services/deviceService';
import {
  clearBasicTierCache,
  isBasicLimitedFromAuthState,
  setAuthBasicTierLimitedHint,
} from '../lib/basicTierLimits';
import { clearBasicTierAccountLockCache } from '../lib/basicTierAccountLock';
import {
  clearBasicTierPlanLockCache,
  clearBasicTierTagCategoryLockCache,
} from '../lib/basicTierResourceLock';
import {
  clearPendingUpgrade,
  consumePaymentSuccessUrlParam,
  consumePendingUpgradeMeta,
  hasPendingUpgrade,
  isPaidPremiumState,
  startSubscriptionUpgradeListener,
} from '../lib/postCheckoutRefresh';
import { capture } from '../lib/posthog';

const getLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDayDifference = (previousDayKey?: string | null, currentDayKey?: string | null) => {
  if (!previousDayKey || !currentDayKey) return null;

  const previousDay = new Date(`${previousDayKey}T00:00:00`);
  const currentDay = new Date(`${currentDayKey}T00:00:00`);

  if (Number.isNaN(previousDay.getTime()) || Number.isNaN(currentDay.getTime())) {
    return null;
  }

  return Math.round((currentDay.getTime() - previousDay.getTime()) / 86400000);
};

const parseTruthyFlag = (value: unknown) => value === true || value === 'true';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isPremium: boolean;
  isTrial: boolean;
  isHomePromo: boolean;
  demarrageSeen: boolean | null;
  isAdmin: boolean;
  isAdminStats: boolean;
  isRedactor: boolean;
  isBetatesteur: boolean;
  isPremiumOnlyMode: boolean;
  premiumOnlyActivationDate: Date | null;
  userCreatedAt: Date | null;
  onboardingCompleted: boolean | null;
  subscriptionType: string;
  isBasic: boolean;
  isTrialExpired: boolean;
  hasActiveTrialOnboarding: boolean;
  /** null = chargement Firestore en cours */
  onboardingDemoLocked: boolean | null;
  patchOnboardingDemoLocked: (locked: boolean) => void;
  refreshUserData: (forceRefreshClaims?: boolean) => Promise<{
    isPremium: boolean;
    isTrial: boolean;
    subscriptionType: string;
  } | null>;
  refreshUser: () => void;
  /** Appareil non reconnu à la connexion : code de vérification envoyé par email, en attente. */
  deviceVerificationPending: { deviceId: string; cooldownSeconds: number } | null;
  /** Appelé par la modale de vérification une fois le code validé (ou pour relancer un nouveau cooldown après renvoi). */
  resolveDeviceVerification: (result?: { cooldownSeconds: number }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [isHomePromo, setIsHomePromo] = useState(false);
  const [demarrageSeen, setDemarrageSeen] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminStats, setIsAdminStats] = useState(false);
  const [isRedactor, setIsRedactor] = useState(false);
  const [isBetatesteur, setIsBetatesteur] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<string>('');
  const [isPremiumOnlyMode, setIsPremiumOnlyMode] = useState(false);
  const [premiumOnlyActivationDate, setPremiumOnlyActivationDate] = useState<Date | null>(null);
  const [userCreatedAt, setUserCreatedAt] = useState<Date | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [onboardingDemoLocked, setOnboardingDemoLocked] = useState<boolean | null>(null);
  const [deviceVerificationPending, setDeviceVerificationPending] = useState<{ deviceId: string; cooldownSeconds: number } | null>(null);
  // Session ouverte via proxy-server/impersonate.mjs (claim `impersonated` signé
  // par le service account dans le custom token — infalsifiable côté client).
  const [isImpersonatedSession, setIsImpersonatedSession] = useState(false);

  const fetchUserData = async (
    userRef: any,
    firebaseUser?: User | null,
    forceRefreshClaims = false,
    fromServer = false,
  ) => {
    // Lire les custom claims en premier (token déjà en cache, pas de réseau)
    let isAdminFromClaims = false;
    let isAdminStatsFromClaims = false;
    let isRedactorFromClaims = false;
    let isBetatesteurFromClaims = false;

    if (firebaseUser) {
      try {
        const idTokenResult = await firebaseUser.getIdTokenResult(forceRefreshClaims);
        const claims = idTokenResult.claims as any;
        isAdminFromClaims = claims.admin === true || claims.isAdmin === true;
        isAdminStatsFromClaims = claims.adminStats === true;
        isRedactorFromClaims = claims.redactor === true || claims.isRedactor === true;
        isBetatesteurFromClaims = claims.betatesteur === true || claims.isBetatesteur === true;
      } catch (_) {
        // Ignore
      }
    }

    let snapshot = { isPremium: false, isTrial: false, subscriptionType: '' };

    try {
      const userDoc = forceRefreshClaims
        ? await getDocFromServer(userRef).catch(() => getDoc(userRef))
        : await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data() as any;
        const nextIsLifetime = userData?.isLifetime === true || userData?.isLifetime === 'true';
        // Un lifetime est TOUJOURS premium, même si le flag isPremium est manquant/incohérent.
        const nextIsPremium = userData?.isPremium === true || userData?.isPremium === 'true' || nextIsLifetime;
        const nextSubscriptionType = userData?.subscriptionType || '';
        const nextIsTrial = parseTruthyFlag(userData?.isTrial);
        snapshot = {
          isPremium: nextIsPremium,
          isTrial: nextIsTrial,
          subscriptionType: nextSubscriptionType,
        };
        setIsPremium(nextIsPremium);
        setSubscriptionType(nextSubscriptionType);
        setIsTrial(nextIsTrial);
        setIsHomePromo(parseTruthyFlag(userData?.homePromo));
        setDemarrageSeen(userData?.demarrageSeen === true);
        setOnboardingDemoLocked(userData?.onboardingDemoLocked === true);
        // isAdmin = 100% custom claims, pas Firestore
        setIsAdmin(isAdminFromClaims);
        setIsAdminStats(isAdminStatsFromClaims);
        setIsRedactor(isRedactorFromClaims || userData?.isRedactor === true || userData?.isRedactor === 'true');
        setIsBetatesteur(isBetatesteurFromClaims || userData?.isBetatesteur === true || userData?.isBetatesteur === 'true');
        // false uniquement si explicitement false (user en cours d'onboarding).
        // Si le champ n'existe pas (utilisateurs existants), on garde true pour la compatibilité.
        // Le serveur garantit maintenant que les nouveaux users ont onboardingCompleted: false dès la vérification email.
        setOnboardingCompleted(userData?.onboardingCompleted === false ? false : true);
        
        // Récupérer la date de création de l'utilisateur
        if (userData?.createdAt) {
          const createdAt = userData.createdAt.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
          setUserCreatedAt(createdAt);
        }
      } else {
        // Pas de doc Firestore, appliquer quand même les claims
        setIsTrial(false);
        setIsHomePromo(false);
        setDemarrageSeen(false);
        setOnboardingDemoLocked(false);
        setIsAdmin(isAdminFromClaims);
        setIsAdminStats(isAdminStatsFromClaims);
        setIsRedactor(isRedactorFromClaims);
        setIsBetatesteur(isBetatesteurFromClaims);
        setSubscriptionType('');
        setOnboardingCompleted(false);
      }
    } catch (error: any) {
      // En cas d'erreur Firestore (token expiré après mise en arrière-plan sur
      // mobile, réseau coupé…), ne pas rétrograder un premium connu en gratuit :
      // cela redirigerait un lifetime vers /pricing. On se rabat sur le cache
      // d'abonnement (écrit par SubscriptionContext) si disponible.
      const cachedUid = firebaseUser?.uid || (userRef && (userRef as any).id);
      const cachedPremium = cachedUid
        ? (localStorage.getItem(`subscription_is_premium_${cachedUid}`) === 'true'
           || localStorage.getItem(`subscription_is_lifetime_${cachedUid}`) === 'true')
        : false;
      setIsPremium(cachedPremium);
      setSubscriptionType('');
      setIsTrial(false);
      setIsHomePromo(false);
      setDemarrageSeen(false);
      setOnboardingDemoLocked(false);
      setIsAdmin(isAdminFromClaims);
      setIsAdminStats(isAdminStatsFromClaims);
      setIsRedactor(isRedactorFromClaims);
      setIsBetatesteur(isBetatesteurFromClaims);
      // Ne jamais laisser PrivateRoute bloqué sur null en cas d'erreur Firestore.
      setOnboardingCompleted(true);
    }

    return snapshot;
  };

  // Fonction pour vérifier le mode premium uniquement
  const checkPremiumOnlyMode = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'appConfig', 'settings'));
      if (configDoc.exists()) {
        const configData = configDoc.data();
        setIsPremiumOnlyMode(configData.premiumOnlyMode === true);
        // console.log supprimé
        
        // Récupérer la date d'activation du mode premium uniquement
        if (configData.activationDate) {
          const activationDate = configData.activationDate.toDate ? configData.activationDate.toDate() : new Date(configData.activationDate);
          setPremiumOnlyActivationDate(activationDate);
        }
      } else {
        // console.log supprimé
        setIsPremiumOnlyMode(false);
      }
    } catch (error) {
      // console.error supprimé
      // En cas d'erreur, désactiver le mode premium uniquement pour éviter de bloquer l'application
      // console.log supprimé
      setIsPremiumOnlyMode(false);
    }
  };

  const refreshUserData = useCallback(async (forceRefreshClaims = false) => {
    if (!user) return null;
    try {
      const userRef = doc(db, 'users', user.uid);
      const snapshot = await fetchUserData(userRef, user, forceRefreshClaims);
      await checkPremiumOnlyMode();
      return snapshot;
    } catch (error) {
      // Erreur non-critique, évite les unhandled rejections dans setInterval
      return null;
    }
  }, [user]);

  const isPremiumRef = useRef(isPremium);
  const isBetatesteurRef = useRef(isBetatesteur);
  const isTrialRef = useRef(isTrial);
  const subscriptionTypeRef = useRef(subscriptionType);
  isPremiumRef.current = isPremium;
  isBetatesteurRef.current = isBetatesteur;
  isTrialRef.current = isTrial;
  subscriptionTypeRef.current = subscriptionType;

  useEffect(() => {
    if (!user) return;

    // Retour Stripe avec ?payment=success / ?upgraded=1 : on (re)marque simplement l'upgrade en
    // attente ; l'event checkout_completed part dans onUpgradeCompleted, une seule fois.
    consumePaymentSuccessUrlParam();

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let pollAttempts = 0;
    let focusDebounce: ReturnType<typeof setTimeout> | null = null;

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      clearPendingUpgrade();
    };

    // checkout_completed (tracking.tsv, P1) : à recouper avec payment_received (webhook).
    // Émis quand l'upgrade marqué avant la redirection Stripe (markPendingUpgrade) est
    // confirmé côté Firestore — les Payment Links Stripe ne renvoient aucun paramètre d'URL
    // exploitable (0 pageview ?payment=success en 60 j, audit 2026-09-20). sendInstantly :
    // PostHogTracker passe en opt-out dès que l'utilisateur devient Premium payant.
    const onUpgradeCompleted = () => {
      if (hasPendingUpgrade()) {
        const meta = consumePendingUpgradeMeta();
        capture(
          'checkout_completed',
          {
            plan: meta?.plan ?? 'premium',
            billing_interval: meta?.billingInterval ?? null,
          },
          { sendInstantly: true },
        );
      }
      stopPolling();
    };

    const isUpgradeComplete = (snapshot?: { isPremium: boolean; isTrial: boolean; subscriptionType: string } | null) =>
      isPaidPremiumState(
        snapshot?.isPremium ?? isPremiumRef.current,
        snapshot?.isTrial ?? isTrialRef.current,
        snapshot?.subscriptionType ?? subscriptionTypeRef.current,
      );

    const startPollingIfPending = async () => {
      if (!hasPendingUpgrade() || pollTimer) return;

      pollAttempts = 0;
      const firstSnapshot = await refreshUserData();
      if (isUpgradeComplete(firstSnapshot)) {
        onUpgradeCompleted();
        return;
      }

      pollTimer = setInterval(async () => {
        pollAttempts += 1;
        const snapshot = await refreshUserData();
        if (isUpgradeComplete(snapshot)) {
          onUpgradeCompleted();
        } else if (pollAttempts >= 20) {
          stopPolling();
        }
      }, 3000);
    };

    const onAppVisible = () => {
      if (document.hidden) return;
      if (focusDebounce) clearTimeout(focusDebounce);
      focusDebounce = setTimeout(() => {
        void refreshUserData();
        void startPollingIfPending();
      }, 300);
    };

    void startPollingIfPending();
    window.addEventListener('focus', onAppVisible);
    document.addEventListener('visibilitychange', onAppVisible);

    const stopSubscriptionSse = startSubscriptionUpgradeListener(() => {
      void (async () => {
        const snapshot = await refreshUserData(true);
        if (isUpgradeComplete(snapshot)) {
          onUpgradeCompleted();
        }
      })();
    });

    return () => {
      stopSubscriptionSse();
      if (pollTimer) clearInterval(pollTimer);
      if (focusDebounce) clearTimeout(focusDebounce);
      window.removeEventListener('focus', onAppVisible);
      document.removeEventListener('visibilitychange', onAppVisible);
    };
  }, [user, refreshUserData]);

  const resetSignedOutState = useCallback(() => {
    setUser(null);
    setIsPremium(false);
    setIsTrial(false);
    setIsHomePromo(false);
    setDemarrageSeen(null);
    setIsAdmin(false);
    setIsAdminStats(false);
    setIsRedactor(false);
    setIsBetatesteur(false);
    setSubscriptionType('');
    setUserCreatedAt(null);
    setPremiumOnlyActivationDate(null);
    setOnboardingCompleted(null);
    setOnboardingDemoLocked(null);
  }, []);

  // Déconnexion propre (voir lib/authSession) : on passe `user` à null de façon
  // synchrone AVANT le signOut Firebase, pour que tous les effects keyés sur
  // `user.uid` (onSnapshot users/{uid}, etc.) se désabonnent tant que le token
  // est encore valide — sinon Firestore renvoie des permission-denied.
  useEffect(() => {
    const onBeforeLogout = () => {
      flushSync(() => resetSignedOutState());
    };
    window.addEventListener(AUTH_BEFORE_LOGOUT_EVENT, onBeforeLogout);
    return () => window.removeEventListener(AUTH_BEFORE_LOGOUT_EVENT, onBeforeLogout);
  }, [resetSignedOutState]);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // console.log supprimé
      try {
        if (!user) {
          setOnboardingDemoLocked(null);
        }
        if (user) {
          const userRef = doc(db, 'users', user.uid);
          // La session peut être coupée pendant nos awaits (ex. signOut du flux 2FA
          // juste après signInWithEmailAndPassword) : ne jamais remonter un `user`
          // périmé, sinon les listeners users/{uid} repartent sans token valide.
          const isStale = () => auth.currentUser?.uid !== user.uid;

          // 1. Charger le profil en premier (isTrial, homePromo, subscriptionType…)
          // Les écritures login/streak ne doivent jamais empêcher cette lecture.
          await fetchUserData(userRef, user);
          if (isStale()) return;
          setUser(user);

          // 2. Mettre à jour streak / lastLogin — non bloquant pour le reste de l'app
          try {
            const userDoc = await getDoc(userRef);
            if (isStale()) return;
            const todayKey = getLocalDateKey();

            if (!userDoc.exists()) {
              setOnboardingCompleted(false);
              await setDoc(userRef, {
                isConnected: true,
                lastLoginAt: serverTimestamp(),
                lastLoginDay: todayKey,
                loginStreak: 1,
                longestLoginStreak: 1,
                email: user.email,
                createdAt: serverTimestamp(),
                isAdmin: false,
                onboardingDemoLocked: false,
                profile: {
                  name: user.displayName || user.email?.split('@')[0] || 'Utilisateur',
                  photoURL: user.photoURL || '',
                  preferences: {}
                },
                ...(user.providerData?.[0]?.providerId === 'google.com' && {
                  authProvider: 'google',
                  emailVerified: user.emailVerified
                })
              });
            } else {
              const existingData = (userDoc.data() as any);
              const previousLoginDay = typeof existingData?.lastLoginDay === 'string' ? existingData.lastLoginDay : null;
              const dayDifference = getDayDifference(previousLoginDay, todayKey);
              const currentLoginStreak = typeof existingData?.loginStreak === 'number' && Number.isFinite(existingData.loginStreak)
                ? existingData.loginStreak
                : 0;
              const longestLoginStreak = typeof existingData?.longestLoginStreak === 'number' && Number.isFinite(existingData.longestLoginStreak)
                ? existingData.longestLoginStreak
                : 0;
              const nextLoginStreak = dayDifference === 0
                ? Math.max(currentLoginStreak, 1)
                : dayDifference === 1
                  ? Math.max(currentLoginStreak, 0) + 1
                  : 1;
              setOnboardingCompleted(existingData?.onboardingCompleted === false ? false : true);

              await setDoc(userRef, {
                isConnected: true,
                lastLoginAt: serverTimestamp(),
                lastLoginDay: todayKey,
                loginStreak: nextLoginStreak,
                longestLoginStreak: Math.max(longestLoginStreak, nextLoginStreak),
                email: user.email,
                ...(user.providerData?.[0]?.providerId === 'google.com' && {
                  emailVerified: user.emailVerified,
                })
              }, { merge: true });

              if (user.providerData?.[0]?.providerId === 'google.com') {
                await updateDoc(userRef, {
                  'profile.photoURL': user.photoURL || ''
                });
              }

              if (!('onboardingCompleted' in existingData) && existingData.onboardingProgress) {
                await setDoc(userRef, { onboardingCompleted: false }, { merge: true });
                setOnboardingCompleted(false);
              }
            }
          } catch {
            // Erreur non fatale : le profil (isTrial, etc.) est déjà chargé ci-dessus.
          }
          
          // Réessayer la récupération des données après un délai si nécessaire
          setTimeout(async () => {
            if (isStale()) return;
            try {
              const retryDoc = await getDoc(userRef);
              if (isStale()) return;
              if (retryDoc.exists()) {
                const retryData = retryDoc.data() as any;
                const currentIsPremium = retryData?.isPremium === true || retryData?.isPremium === 'true';
                const currentIsBetatesteur = retryData?.isBetatesteur === true || retryData?.isBetatesteur === 'true';
                if (
                  currentIsPremium !== isPremiumRef.current
                  || currentIsBetatesteur !== isBetatesteurRef.current
                ) {
                  await fetchUserData(userRef, user);
                }
              }
            } catch (retryError) {
              // ignore
            }
          }, 2000);
        } else {
          resetSignedOutState();
        }
        
        // Vérifier le mode premium uniquement dans tous les cas (authentifié ou non)
        await checkPremiumOnlyMode();
      } catch (error) {
        // Session coupée pendant le chargement (signOut 2FA, révocation…) : l'erreur
        // est un permission-denied attendu, et un setUser(user) ici relancerait les
        // listeners avec un token invalide. Le prochain onAuthStateChanged(null) nettoie.
        if (user && auth.currentUser?.uid === user.uid) {
          setUser(user);
          try {
            const userRef = doc(db, 'users', user.uid);
            await fetchUserData(userRef, user);
          } catch {
            // Dernier recours : ne pas bloquer l'app, sans écraser isTrial/homePromo à false.
            setIsPremium(true);
            setOnboardingCompleted(true);
          }
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  // Appareils connectés (Settings > Sécurité) : enregistre cet appareil et demande
  // une vérification par email s'il n'est pas encore "trusted" côté serveur.
  // Effect indépendant du flux de connexion ci-dessus : un souci réseau/serveur ici
  // ne doit jamais bloquer l'accès à l'app (fail-open) — seul un refus explicite du
  // serveur (requiresCode) déclenche la modale de vérification.
  useEffect(() => {
    if (!user) {
      setDeviceVerificationPending(null);
      setIsImpersonatedSession(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        // Claim posé uniquement par impersonate.mjs via l'Admin SDK (voir plus haut) —
        // lecture du token déjà en cache, aucun aller-retour réseau ici.
        const idTokenResult = await user.getIdTokenResult();
        const impersonated = idTokenResult.claims?.impersonated === true;
        if (cancelled) return;
        setIsImpersonatedSession(impersonated);
        if (impersonated) {
          setDeviceVerificationPending(null);
          return;
        }

        const deviceId = await registerCurrentDevice(user.uid);
        if (cancelled) return;

        const result = await evaluateDevice(deviceId);
        if (cancelled) return;

        setDeviceVerificationPending(
          result.requiresCode ? { deviceId, cooldownSeconds: result.cooldownSeconds ?? 60 } : null
        );
      } catch {
        // Best-effort : indisponibilité réseau/serveur → ne bloque jamais la connexion.
        if (!cancelled) setDeviceVerificationPending(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const isDeviceVerificationPending = !!deviceVerificationPending;

  // Watch de révocation à distance : actif uniquement une fois l'appareil "trusted"
  // (démarre directement si déjà connu, ou dès que la modale de vérification valide
  // le code — cet effect se relance quand isDeviceVerificationPending repasse à false).
  // Jamais démarré pour une session impersonate (aucun appareil n'a été enregistré).
  useEffect(() => {
    if (!user || isDeviceVerificationPending || isImpersonatedSession) return;
    const unsubscribe = watchCurrentDeviceRevocation(user.uid, () => {
      signOutCleanly().catch(() => {});
    });
    return () => unsubscribe();
  }, [user?.uid, isDeviceVerificationPending, isImpersonatedSession]);

  const resolveDeviceVerification = useCallback((result?: { cooldownSeconds: number }) => {
    setDeviceVerificationPending((prev) => {
      if (!result || !prev) return null;
      return { ...prev, cooldownSeconds: result.cooldownSeconds };
    });
  }, []);

  // Force un re-render en créant une nouvelle référence de l'objet user
  const refreshUser = useCallback(() => {
    const currentUser = getAuth().currentUser;
    if (currentUser) setUser(Object.assign(Object.create(Object.getPrototypeOf(currentUser)), currentUser));
  }, []);

  const patchOnboardingDemoLocked = useCallback((locked: boolean) => {
    setOnboardingDemoLocked(locked);
  }, []);

  const isBasic = isPremium && subscriptionType.toLowerCase().includes('basic');

  useEffect(() => {
    if (!user?.uid || loading) {
      setAuthBasicTierLimitedHint(null);
      return;
    }

    const isLimitedTier = isBasicLimitedFromAuthState(
      subscriptionType,
      isPremium,
      isTrial,
      isBetatesteur,
    );

    setAuthBasicTierLimitedHint(isLimitedTier);
    clearBasicTierCache();
    clearBasicTierAccountLockCache();
    clearBasicTierPlanLockCache();
    clearBasicTierTagCategoryLockCache();
  }, [user?.uid, loading, subscriptionType, isPremium, isTrial, isBetatesteur]);

  // isTrial=true + isPremium=false = essai expiré (le flag isTrial n'a pas encore été nettoyé côté serveur)
  const isTrialExpired = isTrial && !isPremium;
  // Essai actif = isTrial=true + isPremium=true. Un abonné payant a donc isPremium=true
  // sans isTrial : le webhook de paiement met isTrial à false mais conserve homePromo
  // (flag d'acquisition, jamais nettoyé). Sans cette exclusion, un user homePromo qui
  // paie reste piégé dans le parcours d'onboarding trial (mur démo + /demarrage forcé).
  const hasPaidSubscription = isPremium && !isTrial;
  const hasActiveTrialOnboarding = (isTrial || isHomePromo) && !isTrialExpired && !hasPaidSubscription;

  const value = useMemo(() => ({
    user,
    loading,
    isPremium,
    isTrial,
    isHomePromo,
    demarrageSeen,
    isAdmin,
    isAdminStats,
    isRedactor,
    isBetatesteur,
    isPremiumOnlyMode,
    premiumOnlyActivationDate,
    userCreatedAt,
    onboardingCompleted,
    subscriptionType,
    isBasic,
    isTrialExpired,
    hasActiveTrialOnboarding,
    onboardingDemoLocked,
    patchOnboardingDemoLocked,
    refreshUserData,
    refreshUser,
    deviceVerificationPending,
    resolveDeviceVerification,
  }), [
    user,
    loading,
    isPremium,
    isTrial,
    isHomePromo,
    demarrageSeen,
    isAdmin,
    isAdminStats,
    isRedactor,
    isBetatesteur,
    isPremiumOnlyMode,
    premiumOnlyActivationDate,
    userCreatedAt,
    onboardingCompleted,
    subscriptionType,
    isBasic,
    isTrialExpired,
    hasActiveTrialOnboarding,
    onboardingDemoLocked,
    patchOnboardingDemoLocked,
    refreshUserData,
    refreshUser,
    deviceVerificationPending,
    resolveDeviceVerification,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
